import React from 'react';

import { get as getConfig } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { useBooleanState } from '@lumapps/lumx-hooks/hooks/useBooleanState';
import { useHover } from '@lumapps/lumx-hooks/hooks/useHover';
import { useLongPress } from '@lumapps/lumx-hooks/hooks/useLongPress';
import { ButtonProps } from '@lumapps/lumx/react';
import {
    REACTION_TYPES,
    ReactionCounts,
    ReactionResourceRef,
    REACTIONS,
    ReactionType,
} from '@lumapps/reactions-core/api/types';
import { isMultiReactionFeatureEnabled } from '@lumapps/reactions-core/ducks/selectors';
import { filterReactionTypes } from '@lumapps/reactions-core/utils/filterReactionTypes';
import { LikeButton } from '@lumapps/reactions-likes/components/LikeButton';
import { PublicLikes } from '@lumapps/reactions-likes/components/PublicLikes';
import { MultiReactionButton } from '@lumapps/reactions-multi/components/MultiReactionButton';
import { PublicMultiReaction } from '@lumapps/reactions-multi/components/PublicMultiReaction';
import { useSelector } from '@lumapps/redux/react';
import { isConnected as isConnectedSelector } from '@lumapps/user/ducks/selectors';
import { browserHasPointerHover } from '@lumapps/utils/browser/browserHasPointerHover';
import { mergeRefs } from '@lumapps/utils/react/mergeRefs';

import { useResourceReaction } from '../../hooks/useResourceReaction';
import { ReactionUserListDialog } from '../ReactionUserListDialog';
import { ReactionUserListMenu } from '../ReactionUserListMenu';

import './index.scss';

export interface ReactButtonProps extends Omit<ButtonProps, 'children'> {
    /** Reaction count */
    reactionCounts: ReactionCounts;
    /** Current active reaction */
    currentUserReactions?: ReactionType[];
    /** Show or hide label */
    showLabel?: boolean;
    /** Resource reference (type & id) */
    resourceRef: ReactionResourceRef;
    /** Whether the initial data is stale (need re-fetch on mount) */
    isInitialStateStale?: boolean;
}

const CLASSNAME_GROUP = 'react-reaction-button-group';

const HOVER_LEAVE_DELAY = 250;

const appId = getConfig().applicationId;

const initialReactionCount: ReactionCounts = { total: 0, details: [] };

/**
 * Generic reaction button with:
 * - on click: toggle reaction
 * - on hover: preview reaction user list
 * - on click see all: open reaction user list dialog
 */
export const ReactButton = React.forwardRef<HTMLButtonElement, ReactButtonProps>((props, ref) => {
    const {
        resourceRef,
        className,
        currentUserReactions = [],
        reactionCounts = initialReactionCount,
        isDisabled: initiallyDisabled,
        isInitialStateStale,
        theme,
        size,
        showLabel,
        ...forwardedProps
    } = props;
    /** Deal FF */
    const hasMultiReaction = useSelector(isMultiReactionFeatureEnabled);
    const isUserConnected = useSelector(isConnectedSelector);

    const buttonRef = React.useRef<HTMLButtonElement | null>(null);
    const menuButtonRef = React.useRef(null);
    const popoverAnchorRef = React.useRef(null);

    const resourceState = React.useMemo(
        () => ({
            currentUserReactions: filterReactionTypes(currentUserReactions),
            reactionCounts: {
                total: reactionCounts.total,
                // TODO: CMS-1169 clean this once the "like" has been removed, use the default currentUseReaction.
                details: reactionCounts.details.filter(({ type }) => REACTION_TYPES.includes(type)),
            },
        }),
        [currentUserReactions, reactionCounts],
    );

    // Handle reaction state
    const { toggleReaction, ...reactionState } = useResourceReaction({
        resourceRef,
        resourceState,
        isInitialStateStale,
    });

    /**
     * Natively disabling a focused button blurs it: the browser drops focus, which falls back to
     * `<body>`, so a keyboard user loses their place on every reaction. The transient request state
     * is therefore exposed through `aria-disabled`, which still blocks activation (see the DS
     * `RawClickable`) while keeping the button focusable. Only a caller-provided `isDisabled`
     * (e.g. preview mode) disables it for real.
     */
    const isDisabled = initiallyDisabled;
    const isInteractionDisabled = Boolean(initiallyDisabled || reactionState.isLoading);

    const [isReactionOpen, setIsReactionOpen] = React.useState<boolean | undefined>(undefined);

    // Handle hover or long press (based on browser capabilities)
    const hasHover = browserHasPointerHover();
    const hover = useHover({ disable: !hasHover && isInteractionDisabled, leaveDelay: HOVER_LEAVE_DELAY });
    const longPress = useLongPress({ disable: hasHover });

    // Reaction UserList dialog
    const [isDialogOpen, , closeDialog, openDialog] = useBooleanState(false);

    // Do not show the reaction list menu when there is no reactions or on the webview app or the user is not logged.
    /**
     * Only show the reaction list on the following cases :
     * - There is reactions
     * - You are not on the webview
     * - You are not on the designer
     * - The user is logged
     */
    const showReactionList = reactionState.reactionCounts.total && appId !== AppId.webview && appId !== AppId.designer;

    const reactionRef = React.useMemo(() => {
        if (!isUserConnected) {
            return undefined;
        }
        return mergeRefs([ref, buttonRef, hover.registerElement, longPress.registerElement]);
    }, [isUserConnected, ref, hover.registerElement, longPress.registerElement]);

    const onToggleLike = React.useCallback(() => toggleReaction(REACTIONS.THUMBS_UP), [toggleReaction]);

    const LikeWrapper = React.useMemo(() => (isUserConnected ? LikeButton : PublicLikes), [isUserConnected]);
    const MultiReactionWrapper = React.useMemo(
        () => (isUserConnected ? MultiReactionButton : PublicMultiReaction),
        [isUserConnected],
    );

    return (
        <>
            <div className={CLASSNAME_GROUP} ref={popoverAnchorRef}>
                {!hasMultiReaction ? (
                    <LikeWrapper
                        ref={reactionRef}
                        className={className}
                        onClick={onToggleLike}
                        isDisabled={isDisabled}
                        aria-disabled={isInteractionDisabled || undefined}
                        currentUserReactions={reactionState.currentUserReactions}
                        reactionCounts={reactionState.reactionCounts}
                        theme={theme}
                        size={size}
                        showLabel={showLabel}
                        {...forwardedProps}
                    />
                ) : (
                    <MultiReactionWrapper
                        mergedRefs={reactionRef}
                        className={className}
                        toggleReaction={toggleReaction}
                        onToggle={setIsReactionOpen}
                        isDisabled={isDisabled}
                        aria-disabled={isInteractionDisabled || undefined}
                        currentUserReactions={reactionState.currentUserReactions}
                        reactionCounts={reactionState.reactionCounts}
                        theme={theme}
                        size={size}
                        isOpen={isReactionOpen}
                        buttonRef={buttonRef}
                        {...forwardedProps}
                    />
                )}

                {showReactionList && isUserConnected ? (
                    <ReactionUserListMenu
                        resourceRef={resourceRef}
                        menuButtonRef={menuButtonRef}
                        popoverAnchorRef={popoverAnchorRef}
                        theme={theme}
                        onSeeAll={openDialog}
                        shouldOpen={
                            !isReactionOpen &&
                            !isDialogOpen &&
                            (hover.isHovered || longPress.isLongPressed) &&
                            !isInteractionDisabled
                        }
                        hoverRef={hover.registerElement}
                        reactionType={hasMultiReaction ? undefined : REACTIONS.THUMBS_UP}
                        size={size}
                        showUserReaction={hasMultiReaction}
                    />
                ) : null}
            </div>

            {isUserConnected && (
                <ReactionUserListDialog
                    resourceRef={resourceRef}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                    reactionCounts={reactionState.reactionCounts}
                    parentElement={menuButtonRef}
                    reactionType={hasMultiReaction ? undefined : REACTIONS.THUMBS_UP}
                    showUserReaction={hasMultiReaction}
                />
            )}
        </>
    );
});
